import { useState, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { usePortalLogout } from '@/hooks/usePortalLogout';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';
import { getProfileDisplayName } from '@/lib/profileDisplayName';
import { JPH_APP_BUILD_LABEL } from '@/lib/appBuild';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import LogoutConfirmModal from '@/components/feature/LogoutConfirmModal';
import ChangePasswordModal from '@/components/feature/ChangePasswordModal';
import SidebarAccountMenu from '@/components/feature/SidebarAccountMenu';
import SidebarProjectsNavSection from '@/components/feature/SidebarProjectsNavSection';

interface MgmtLayoutProps {
  children: React.ReactNode;
  title?: string;
  showBack?: boolean;
  backTo?: string;
  actions?: React.ReactNode;
}

export default function MgmtLayout({ children, title, showBack, backTo, actions }: MgmtLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { profile, user, loading: authLoading } = useAuth();
  const accountName = authLoading
    ? '...'
    : getProfileDisplayName(profile, t('user.management'), user?.email);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const {
    logoutConfirm,
    openLogoutConfirm,
    closeLogoutConfirm,
    performLogout,
    isSigningOut,
    logoutError,
  } = usePortalLogout();
  const { unreadCount } = useUnreadNotifications();
  const languageSwitcherProps = {
    userType: profile?.user_type,
    profileId: profile?.id,
  } as const;

  const projectsNavActive = location.pathname.startsWith('/management/projects');
  const projectIdMatch = location.pathname.match(/\/management\/projects\/([^/]+)/);
  const currentProjectId = projectIdMatch?.[1];
  const onScheduleTab = new URLSearchParams(location.search).get('tab') === 'Schedule';

  const buildMgmtProjectUrl = useCallback(
    (projectId: string) => {
      if (onScheduleTab && currentProjectId) {
        return `/management/projects/${projectId}?tab=Schedule`;
      }
      return `/management/projects/${projectId}`;
    },
    [onScheduleTab, currentProjectId]
  );

  const navItems = [
    { to: '/management/dashboard', icon: 'ri-dashboard-line', label: t('nav.dashboard') },
    { to: '/management/projects', icon: 'ri-building-2-line', label: t('nav.projects') },
    { to: '/management/instructions', icon: 'ri-file-list-3-line', label: t('nav.instructions') },
    { to: '/management/change-orders', icon: 'ri-exchange-line', label: t('nav.changeOrders') },
    { to: '/management/daily-log', icon: 'ri-calendar-line', label: t('nav.dailyLog') },
    { to: '/management/notifications', icon: 'ri-notification-3-line', label: t('nav.alerts') },
  ];

  const actionItems = [
    { to: '/management/projects/new', icon: 'ri-add-circle-line', label: t('nav.newProject') },
    { to: '/management/schedule-templates', icon: 'ri-layout-grid-line', label: t('nav.scheduleTemplates') },
  ];

  const isActionActive = (to: string) => {
    if (to === '/management/projects/new') {
      return location.pathname === '/management/projects/new';
    }
    if (to === '/management/schedule-templates') {
      return (
        location.pathname === '/management/schedule-templates' ||
        location.pathname === '/management/templates'
      );
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-[#F5F5F3] flex overflow-x-hidden">

      {/* ── Desktop Sidebar ── */}
      <aside className="hidden lg:flex flex-col w-60 bg-[#1A1A1A] fixed top-0 left-0 bottom-0 z-40">
        {/* Logo block */}
        <div
          className="flex items-center justify-center px-5 cursor-pointer flex-shrink-0"
          style={{ background: '#FFFFFF', height: '64px', borderBottom: '1px solid #E5E2DC' }}
          onClick={() => navigate('/management/dashboard')}
        >
          <img
            src="https://qsenozfawbynwkmpxyjr.supabase.co/storage/v1/object/public/Logo/Logo_portal.png"
            alt="JPH Portal"
            style={{ maxHeight: '36px', maxWidth: '160px', objectFit: 'contain' }}
          />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest px-2 mb-2">{t('nav.menu')}</p>
          {navItems.map((item) => (
            <div key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#E8621A]/15 text-white border-l-2 border-[#E8621A]'
                      : 'text-white/50 hover:bg-white/5 hover:text-white border-l-2 border-transparent'
                  }`
                }
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <span className="text-sm font-medium">{item.label}</span>
                {item.to === '/management/notifications' && unreadCount > 0 && (
                  <span className="ml-auto w-2 h-2 rounded-full bg-[#E8621A]"></span>
                )}
              </NavLink>
              {item.to === '/management/projects' && (
                <SidebarProjectsNavSection
                  active={projectsNavActive}
                  profileId={profile?.id ?? user?.id}
                  userType={profile?.user_type}
                  profileLoading={authLoading}
                  currentProjectId={currentProjectId}
                  buildProjectUrl={buildMgmtProjectUrl}
                />
              )}
            </div>
          ))}

          <div className="pt-4">
            <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest px-2 mb-2">{t('nav.actions')}</p>
            {actionItems.map((item) => {
              const active = isActionActive(item.to);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer border-l-2 ${
                    active
                      ? 'bg-[#E8621A]/15 text-white border-[#E8621A]'
                      : 'text-white/50 hover:bg-white/5 hover:text-white border-transparent'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <span className="text-sm font-medium">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>

        <div className="border-t border-white/5 px-3 py-3">
          {/* Language switcher in sidebar */}
          <div className="px-3 pb-2">
            <LanguageSwitcher compact {...languageSwitcherProps} />
          </div>
          <SidebarAccountMenu
            displayName={accountName}
            loading={authLoading}
            portalLabel={t('user.jphPortal')}
            onChangePassword={() => setChangePasswordOpen(true)}
            onSignOut={openLogoutConfirm}
          />
          <p
            className="text-center mt-2"
            style={{ color: '#8A8A8A', fontSize: '10px' }}
            aria-label={`Application version ${JPH_APP_BUILD_LABEL}`}
            title={`Application build ${JPH_APP_BUILD_LABEL}`}
          >
            Version {JPH_APP_BUILD_LABEL}
          </p>
          <p className="text-center mt-1 pb-1" style={{ color: '#BBBBBB', fontSize: '11px' }}>
            {t('user.producedBy')}
          </p>
        </div>
      </aside>

      {/* ── Mobile overlay sidebar ── */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)}></div>
          <aside className="relative w-64 bg-[#1A1A1A] flex flex-col h-full">
            <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E2DC' }}>
              <div className="flex items-center justify-between px-5" style={{ height: '64px' }}>
                <img
                  src="https://qsenozfawbynwkmpxyjr.supabase.co/storage/v1/object/public/Logo/Logo_portal.png"
                  alt="JPH Portal"
                  style={{ maxHeight: '32px', maxWidth: '140px', objectFit: 'contain' }}
                />
                <button onClick={() => setSidebarOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer flex-shrink-0">
                  <i className="ri-close-line text-[#374151] text-lg"></i>
                </button>
              </div>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {navItems.map((item) => (
                <div key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${
                        isActive ? 'bg-[#E8621A]/15 text-white' : 'text-white/50 hover:bg-white/5 hover:text-white'
                      }`
                    }
                  >
                    <i className={`${item.icon} text-lg`}></i>
                    <span className="text-sm font-medium">{item.label}</span>
                    {item.to === '/management/notifications' && unreadCount > 0 && (
                      <span className="ml-auto w-2 h-2 rounded-full bg-[#E8621A]"></span>
                    )}
                  </NavLink>
                  {item.to === '/management/projects' && (
                    <SidebarProjectsNavSection
                      active={projectsNavActive}
                      profileId={profile?.id ?? user?.id}
                      userType={profile?.user_type}
                      profileLoading={authLoading}
                      currentProjectId={currentProjectId}
                      buildProjectUrl={buildMgmtProjectUrl}
                      onProjectClick={() => setSidebarOpen(false)}
                    />
                  )}
                </div>
              ))}
              <div className="pt-4">
                <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest px-2 mb-2">{t('nav.actions')}</p>
                {actionItems.map((item) => {
                  const active = isActionActive(item.to);
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${
                        active ? 'bg-[#E8621A]/15 text-white' : 'text-white/50 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <i className={`${item.icon} text-lg`}></i>
                      <span className="text-sm font-medium">{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </nav>
            <div className="border-t border-white/5 px-3 py-3">
              <div className="px-3 pb-2">
                <LanguageSwitcher compact {...languageSwitcherProps} />
              </div>
              <SidebarAccountMenu
                displayName={accountName}
                loading={authLoading}
                portalLabel={t('user.jphPortal')}
                onChangePassword={() => setChangePasswordOpen(true)}
                onSignOut={openLogoutConfirm}
                onAfterAction={() => setSidebarOpen(false)}
              />
              <p className="text-center mt-2 pb-1" style={{ color: '#BBBBBB', fontSize: '11px' }}>
                {t('user.producedBy')}
              </p>
            </div>
          </aside>
        </div>
      )}

      {/* ── Main area ── */}
      <div className="flex-1 flex flex-col lg:ml-60 min-h-screen min-w-0 max-w-full overflow-x-hidden management-mobile-safe">

        {/* ── Top Header ── */}
        <header
          className="flex-shrink-0 z-30 flex flex-col"
          style={{
            background: '#FFFFFF',
            borderBottom: '1px solid #E5E2DC',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <div className="flex items-center px-4 lg:px-6 gap-3" style={{ height: '64px' }}>
            <div className="flex items-center gap-0 flex-1 min-w-0">
              {showBack ? (
                <button
                  onClick={() => navigate(backTo || (-1 as never))}
                  className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer flex-shrink-0 mr-2"
                  style={{ color: '#374151' }}
                >
                  <i className="ri-arrow-left-line text-lg"></i>
                </button>
              ) : (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer flex-shrink-0 mr-2"
                  style={{ color: '#374151' }}
                >
                  <i className="ri-menu-line text-lg"></i>
                </button>
              )}

              <span
                className="hidden lg:inline-flex items-center flex-shrink-0"
                style={{
                  background: '#FFF0E8',
                  color: '#F05A00',
                  border: '1px solid #F05A00',
                  fontSize: '11px',
                  padding: '3px 12px',
                  borderRadius: '20px',
                  fontWeight: 600,
                  letterSpacing: '0.3px',
                }}
              >
                {t('nav.management')}
              </span>

              {title && (
                <h1 className="text-[15px] font-bold text-[#1A1A1A] truncate lg:ml-4 ml-0">{title}</h1>
              )}
            </div>

            {/* Right: language switcher + actions + notifications */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <LanguageSwitcher {...languageSwitcherProps} />
              {actions}
              <button
                onClick={() => navigate('/management/notifications')}
                className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer relative"
                style={{ color: '#374151' }}
              >
                <i className="ri-notification-3-line text-lg"></i>
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#E8621A] rounded-full"></span>
                )}
              </button>
            </div>
          </div>

          {/* Orange accent line */}
          <div style={{ height: '3px', background: '#F05A00', width: '100%' }} />
        </header>

        {/* Page content */}
        {/* Mobile: pad bottom so content clears the fixed bottom nav. Desktop: no padding needed (no bottom nav). */}
        <main className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
          {children}
          {/* Mobile bottom spacer — keeps content above the fixed bottom nav */}
          <div className="lg:hidden flex-shrink-0" style={{ height: 'calc(100px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />
        </main>
      </div>

      <LogoutConfirmModal
        open={logoutConfirm}
        isSigningOut={isSigningOut}
        error={logoutError}
        onCancel={closeLogoutConfirm}
        onConfirm={performLogout}
      />

      <ChangePasswordModal
        open={changePasswordOpen}
        email={user?.email ?? ''}
        onClose={() => setChangePasswordOpen(false)}
        onSignInAgain={performLogout}
      />

      {/* ── Mobile bottom tab bar ── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 flex items-center z-40" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', minHeight: '64px' }}>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 cursor-pointer transition-colors ${
                isActive ? 'text-[#E8621A]' : 'text-[#9CA3AF]'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className="w-6 h-6 flex items-center justify-center relative">
                  <i className={`${item.icon} text-xl`}></i>
                  {item.to === '/management/notifications' && unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#E8621A] rounded-full"></span>
                  )}
                </div>
                <span className={`text-[10px] font-medium ${isActive ? 'text-[#E8621A]' : 'text-[#9CA3AF]'}`}>
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
